"use client";

import Image from "next/image";
import { CATEGORY_LABELS, getLocalizedField, type Course } from "../api";
import type { Locale } from "@/lib/i18n";

type Props = {
  course: Pick<
    Course,
    "title_en" | "title_bn" | "category"
  >;
  locale: Locale;
  /** Use a larger title size in sparse/hero placements (homepage featured). */
  emphasis?: "default" | "featured";
  /**
   * When false, only category + brand mark render (card shows the title below).
   * Avoids duplicating the title on catalog cards.
   */
  showTitle?: boolean;
};

/**
 * Shared, branded 16:9 course thumbnail fallback. Used when:
 *  - `course.thumbnailImageId` is absent / empty
 *  - the uploaded asset is known bad (e.g. baked-in sale graphics)
 *
 * Visual: Gray-100 base with a very low-saturation diagonal brand tint,
 * muted eyebrow category, 2-line-clamped title, subtle Progsity icon logo.
 * No hype imagery, no price text, no promotional graphics.
 */
export function CourseThumbnailFallback({
  course,
  locale,
  emphasis = "default",
  showTitle = true,
}: Props) {
  const title =
    getLocalizedField(course.title_en, course.title_bn, locale) ?? course.title_en;
  const categoryLabel = CATEGORY_LABELS[course.category] ?? course.category;

  const titleClass =
    emphasis === "featured"
      ? "line-clamp-2 text-lg font-semibold leading-snug"
      : "line-clamp-2 text-[17px] font-semibold leading-snug";

  return (
    <div
      className="relative flex h-full w-full flex-col justify-between overflow-hidden bg-[#F3F4F6] p-5"
      role="img"
      aria-label={title}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[rgba(233,77,29,0.06)] via-transparent to-[rgba(233,77,29,0.025)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[rgba(233,77,29,0.05)] blur-2xl"
      />

      {showTitle && (
        <span className="relative z-10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#B2431A]">
          {categoryLabel}
        </span>
      )}

      {showTitle ? (
        <h4 className={`relative z-10 ${titleClass} text-[#111111]`}>{title}</h4>
      ) : (
        <div className="relative z-10 flex-1" aria-hidden />
      )}

      <div className="relative z-10 flex items-end justify-end">
        <Image
          src="/logo-icon.svg"
          alt=""
          width={16}
          height={16}
          className="h-4 w-4 opacity-70"
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
